import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import React, {useEffect, useState} from "react"
import {AsyncResultModal} from "../AsyncResultModal/AsyncResultModal"
import {ApplicationDocument, DocumentTarget, Officer} from "../../resources/application"
import {BeneficialOwnerResource} from "../../resources/beneficialOwner"
import {ErrorDocument, OkDocument} from "../../resources/common"
import {Result} from "neverthrow"
import {useAccessToken} from "../../services/auth"

export enum DocType {
	passport = "passport",
	idDocument = "idDocument",
	socialSecurityCard = "socialSecurityCard",
	addressVerification = "addressVerification",
	selfieVerification = "selfieVerification",
	certificateOfIncorporation = "certificateOfIncorporation",
	employerIdentificationNumberConfirmation = "employerIdentificationNumberConfirmation",
	matriculaConsularCard = "matriculaConsularCard",
	operatingAddressVerification = "operatingAddressVerification",
}

type IndividualDocType =
	| DocType.socialSecurityCard
	| DocType.idDocument
	| DocType.passport
	| DocType.addressVerification
	| DocType.matriculaConsularCard
	| DocType.selfieVerification
type BusinessDocType =
	| DocType.certificateOfIncorporation
	| DocType.employerIdentificationNumberConfirmation
	| DocType.addressVerification
	| DocType.operatingAddressVerification

export type RequestDocumentFn = (
	accessToken: string,
	applicationId: string,
	documentType: string,
	target?: DocumentTarget
) => Promise<Result<OkDocument<ApplicationDocument>, ErrorDocument>>

export interface IndividualRequestDocumentModalProps {
	applicationId: string
	ssn?: string
	passport?: string
	matriculaConsular?: string
	close: () => void
	onSuccess: () => void
	requestFn: RequestDocumentFn
}

export function IndividualRequestDocumentModal({
	applicationId,
	ssn,
	passport,
	matriculaConsular,
	close,
	onSuccess,
	requestFn,
}: IndividualRequestDocumentModalProps) {
	const accessToken = useAccessToken()
	const [requestDocumentState, request] = useAsyncResultIdle(requestFn)
	const [docType, setDocType] = useState<IndividualDocType>(DocType.addressVerification)

	return (
		<AsyncResultModal
			title="Request document"
			close={close}
			state={requestDocumentState}
			onSubmit={() => {
				request(accessToken, applicationId, docType, {applicationId})
			}}
			onSuccess={() => {
				close()
				onSuccess()
			}}
			successText="Requested"
			buttonText="Request"
			buttonClassname="button is-info"
			errorToText={(err: ErrorDocument) => (err.errors ? err.errors[0].title : "An unexpected error has occurred")}
		>
			<div className="control">
				{"Document: "}
				<select value={docType} onChange={(e) => setDocType(e.target.value as IndividualDocType)}>
					<option key={DocType.addressVerification} value={DocType.addressVerification}>
						Address Verification
					</option>
					<option key={DocType.selfieVerification} value={DocType.selfieVerification}>
						Selfie Verification
					</option>
					{ssn ? (
						<option key={DocType.socialSecurityCard} value={DocType.socialSecurityCard}>
							SSN
						</option>
					) : undefined}
					{matriculaConsular ? (
						<option key={DocType.matriculaConsularCard} value={DocType.matriculaConsularCard}>
							Matricula Consular
						</option>
					) : undefined}
					{ssn ? (
						<option key={DocType.idDocument} value={DocType.idDocument}>
							ID
						</option>
					) : undefined}
					{passport ? (
						<option key={DocType.passport} value={DocType.passport}>
							Passport
						</option>
					) : undefined}
				</select>
			</div>
		</AsyncResultModal>
	)
}

type Entities = "business" | "officer" | "bo1" | "bo2" | "bo3" | "bo4"

export interface BusinessRequestDocumentModalProps {
	applicationId: string
	officer?: Officer
	officerRelationshipId?: string
	beneficialOwners?: BeneficialOwnerResource[]
	hasOperatingAddress: boolean
	showPersonEntities: boolean
	close: () => void
	onSuccess: () => void
	requestFn: RequestDocumentFn
}

export function BusinessRequestDocumentModal({
	applicationId,
	officer,
	officerRelationshipId,
	hasOperatingAddress,
	showPersonEntities,
	close,
	beneficialOwners,
	onSuccess,
	requestFn,
}: BusinessRequestDocumentModalProps) {
	const accessToken = useAccessToken()
	const [requestDocumentState, request] = useAsyncResultIdle(requestFn)
	const [entity, setEntity] = useState<Entities>("business")
	const [docType, setDocType] = useState<IndividualDocType | BusinessDocType>(DocType.addressVerification)

	useEffect(() => {
		if (
			entity !== "business" &&
			(docType === DocType.employerIdentificationNumberConfirmation ||
				docType === DocType.certificateOfIncorporation ||
				docType === DocType.operatingAddressVerification)
		) {
			setDocType(DocType.addressVerification)
		} else if (
			entity === "business" &&
			docType !== DocType.employerIdentificationNumberConfirmation &&
			docType !== DocType.certificateOfIncorporation &&
			docType !== DocType.operatingAddressVerification &&
			docType !== DocType.addressVerification
		) {
			setDocType(DocType.certificateOfIncorporation)
		}
	}, [entity])

	function getTarget(): DocumentTarget | undefined {
		switch (entity) {
			case "business":
				return {applicationId}
			case "officer":
				return officerRelationshipId ? {officerId: officerRelationshipId} : undefined
			case "bo1":
				return beneficialOwners?.[0] ? {beneficialOwnerId: beneficialOwners[0].id} : undefined
			case "bo2":
				return beneficialOwners?.[1] ? {beneficialOwnerId: beneficialOwners[1].id} : undefined
			case "bo3":
				return beneficialOwners?.[2] ? {beneficialOwnerId: beneficialOwners[2].id} : undefined
			case "bo4":
				return beneficialOwners?.[3] ? {beneficialOwnerId: beneficialOwners[3].id} : undefined
		}
	}

	function getEntity() {
		switch (entity) {
			case "business":
				return undefined
			case "officer":
				return officer
			case "bo1":
				return beneficialOwners?.[0]?.attributes
			case "bo2":
				return beneficialOwners?.[1]?.attributes
			case "bo3":
				return beneficialOwners?.[2]?.attributes
			case "bo4":
				return beneficialOwners?.[3]?.attributes
		}
	}

	const selectedEntity = getEntity()

	return (
		<AsyncResultModal
			title="Request document"
			close={close}
			state={requestDocumentState}
			onSubmit={() => {
				const target = getTarget()
				request(accessToken, applicationId, docType, target)
			}}
			onSuccess={() => {
				close()
				onSuccess()
			}}
			successText="Requested"
			buttonText="Request"
			buttonClassname="button is-info"
			errorToText={(err: ErrorDocument) => (err.errors ? err.errors[0].title : "An unexpected error has occurred")}
		>
			<div className="control mb-2">
				{"Entity: "}
				<select value={entity} onChange={(e) => setEntity(e.target.value as Entities)}>
					<option key="business" value="business">
						Business
					</option>
					{showPersonEntities && officer && (
						<option
							key="officer"
							value="officer"
						>{`Officer - ${officer.fullName.first} ${officer.fullName.last}`}</option>
					)}
					{showPersonEntities && beneficialOwners && beneficialOwners.length > 0 ? (
						<option key="bo1" value="bo1">
							{`BO - ${beneficialOwners[0]?.attributes.fullName.first} ${beneficialOwners[0]?.attributes.fullName.last}`}
						</option>
					) : undefined}
					{showPersonEntities && beneficialOwners && beneficialOwners.length > 1 ? (
						<option key="bo2" value="bo2">
							{`BO - ${beneficialOwners[1]?.attributes.fullName.first} ${beneficialOwners[1]?.attributes.fullName.last}`}
						</option>
					) : undefined}
					{showPersonEntities && beneficialOwners && beneficialOwners.length > 2 ? (
						<option key="bo3" value="bo3">
							{`BO - ${beneficialOwners[2]?.attributes.fullName.first} ${beneficialOwners[2]?.attributes.fullName.last}`}
						</option>
					) : undefined}
					{showPersonEntities && beneficialOwners && beneficialOwners.length > 3 ? (
						<option key="bo4" value="bo4">
							{`BO - ${beneficialOwners[3]?.attributes.fullName.first} ${beneficialOwners[3]?.attributes.fullName.last}`}
						</option>
					) : undefined}
				</select>
			</div>
			<div className="control">
				{"Document: "}
				<select value={docType} onChange={(e) => setDocType(e.target.value as IndividualDocType)}>
					{entity !== "business" ? (
						<option key={DocType.selfieVerification} value={DocType.selfieVerification}>
							Selfie Verification
						</option>
					) : null}
					{entity !== "business" ? (
						<option key={DocType.addressVerification} value={DocType.addressVerification}>
							Address Verification
						</option>
					) : null}
					{entity !== "business" && selectedEntity?.ssn ? (
						<option key={DocType.socialSecurityCard} value={DocType.socialSecurityCard}>
							SSN
						</option>
					) : undefined}
					{entity !== "business" && selectedEntity?.ssn ? (
						<option key={DocType.idDocument} value={DocType.idDocument}>
							ID
						</option>
					) : undefined}
					{entity !== "business" && selectedEntity?.matriculaConsular ? (
						<option key={DocType.matriculaConsularCard} value={DocType.matriculaConsularCard}>
							Matricula Consular
						</option>
					) : undefined}
					{entity !== "business" && selectedEntity?.passport ? (
						<option key={DocType.passport} value={DocType.passport}>
							Passport
						</option>
					) : undefined}
					{entity === "business" ? (
						<option key={DocType.certificateOfIncorporation} value={DocType.certificateOfIncorporation}>
							Certificate Of Incorporation
						</option>
					) : undefined}
					{entity === "business" ? (
						<option key={DocType.addressVerification} value={DocType.addressVerification}>
							Address Verification
						</option>
					) : null}
					{entity === "business" && hasOperatingAddress ? (
						<option key={DocType.operatingAddressVerification} value={DocType.operatingAddressVerification}>
							Operating Address Verification
						</option>
					) : null}
					{entity === "business" ? (
						<option
							key={DocType.employerIdentificationNumberConfirmation}
							value={DocType.employerIdentificationNumberConfirmation}
						>
							Employer Identification Number
						</option>
					) : undefined}
				</select>
			</div>
		</AsyncResultModal>
	)
}
